<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import FlightSummaryDTO = Modules.CampaignPlanner.Data.FlightSummaryDTO;

const props = defineProps<{
	flight: FlightSummaryDTO;
}>();

const emit = defineEmits<{
	updated: [flight: FlightSummaryDTO];
}>();

const isDialogVisible = defineModel<boolean>();

type PricingMode = "pqa" | "price" | "cpm";

const mode = ref<PricingMode>("pqa");
const priceInput = ref<number | null>(null);
const cpmInput = ref<number | null>(null);
const formRef = ref();
const saving = ref(false);

onMounted(() => {
	if (props.flight.cost_overwrite !== null) {
		mode.value = "price";
		priceInput.value = props.flight.cost_overwrite;
	} else if (props.flight.cpm_overwrite !== null) {
		mode.value = "cpm";
		cpmInput.value = props.flight.cpm_overwrite;
	} else {
		mode.value = "pqa";
	}
});

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("nl-NL", {
		style: "currency",
		currency: "EUR",
		minimumFractionDigits: 2,
	}).format(value);

const overwrittenPrice = computed<number | null>(() => {
	if (mode.value === "price" && priceInput.value !== null) {
		return priceInput.value;
	}
	if (mode.value === "cpm" && cpmInput.value !== null) {
		return (cpmInput.value / 1000) * props.flight.total_reach;
	}
	return null;
});

const priceDelta = computed<number | null>(() => {
	if (overwrittenPrice.value === null) return null;
	return overwrittenPrice.value - props.flight.base_total_price;
});

const deltaColor = computed(() => {
	if (priceDelta.value === null) return undefined;
	if (priceDelta.value === 0) return "success";
	return priceDelta.value > 0 ? "warning" : "error";
});

const numberRules = [
	(v: number | null) => v !== null || "Required",
	(v: number | null) => (v !== null && v > 0) || "Must be greater than 0",
];

const save = async () => {
	if (mode.value !== "pqa") {
		const { valid } = await formRef.value.validate();
		if (!valid) return;
	}

	saving.value = true;

	const payload =
		mode.value === "pqa"
			? { cost_overwrite: null, cpm_overwrite: null }
			: mode.value === "price"
			? { cost_overwrite: priceInput.value, cpm_overwrite: null }
			: { cost_overwrite: null, cpm_overwrite: cpmInput.value };

	const response = await toastApiResult(
		useApiClient("campaignPlanner").updateFlightPricingOverwrite(
			props.flight.uuid,
			payload
		)
	);

	if (response.isOk()) {
		emit("updated", response.value);
		isDialogVisible.value = false;
	}

	saving.value = false;
};
</script>

<template>
	<v-dialog v-model="isDialogVisible" max-width="480" persistent>
		<v-card>
			<v-toolbar color="lightprimary" elevation="0" flat>
				<template #prepend>
					<v-icon class="ml-3">fa: fa-light fa-circle-euro</v-icon>
				</template>
				<v-toolbar-title>Overwrite PQA Pricing</v-toolbar-title>
				<template #append>
					<v-btn icon variant="text" @click="isDialogVisible = false">
						<v-icon>fa: fa-regular fa-xmark</v-icon>
					</v-btn>
				</template>
			</v-toolbar>

			<v-divider />

			<v-card-text class="pa-6">
				<v-btn-toggle
					v-model="mode"
					mandatory
					density="compact"
					variant="outlined"
					color="primary"
					class="mb-5"
				>
					<v-btn value="pqa">Use PQA Result</v-btn>
					<v-btn value="price">Overwrite Price</v-btn>
					<v-btn value="cpm">Overwrite CPM</v-btn>
				</v-btn-toggle>

				<p v-if="mode === 'pqa'" class="text-body-2 text-medium-emphasis mb-0">
					Saves the flight with the original PQA result. Any existing price or
					CPM overwrite will be removed.
				</p>

				<v-form ref="formRef">
					<template v-if="mode === 'price'">
						<div class="field-label text-caption text-medium-emphasis mb-1">
							Price (€)
						</div>
						<v-text-field
							v-model.number="priceInput"
							type="number"
							variant="filled"
							density="compact"
							hide-details="auto"
							:rules="numberRules"
						/>
					</template>

					<template v-if="mode === 'cpm'">
						<div class="field-label text-caption text-medium-emphasis mb-1">
							CPM (€)
						</div>
						<v-text-field
							v-model.number="cpmInput"
							type="number"
							variant="filled"
							density="compact"
							hide-details="auto"
							:rules="numberRules"
						/>
					</template>
				</v-form>

				<div v-if="mode !== 'pqa' && overwrittenPrice !== null">
					<div class="pricing-preview mt-5">
						<div class="preview-row">
							<span class="preview-label">Original price</span>
							<span class="preview-value">{{
								formatCurrency(flight.base_total_price)
							}}</span>
						</div>
						<div class="preview-row">
							<span class="preview-label">Overwritten price</span>
							<span class="preview-value">{{
								formatCurrency(overwrittenPrice)
							}}</span>
						</div>
						<v-divider class="my-2" />
						<div class="preview-row">
							<span class="preview-label">Change</span>
							<v-chip
								size="small"
								:color="deltaColor"
								variant="tonal"
								class="font-weight-medium"
							>
								{{ formatCurrency(priceDelta!) }}
							</v-chip>
						</div>
					</div>
					<v-alert
						v-if="priceDelta !== null && priceDelta < 0"
						class="mt-5"
						:icon="false"
						color="warning"
					>
						<span style="font-size: 12px"
							>Requires approval in Salesforce after sync.</span
						>
					</v-alert>
				</div>
			</v-card-text>

			<v-divider />

			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" @click="isDialogVisible = false">Cancel</v-btn>
				<v-btn variant="tonal" color="primary" :loading="saving" @click="save"
					>Save</v-btn
				>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
.pricing-preview {
	border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
	border-radius: 8px;
	padding: 12px 16px;
}

.preview-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 3px 0;
}

.preview-label {
	font-size: 0.875rem;
	color: rgba(var(--v-theme-on-surface), 0.6);
}

.preview-value {
	font-size: 0.875rem;
	font-weight: 500;
}

.field-label {
	font-size: 0.75rem;
	letter-spacing: 0.03em;
	text-transform: uppercase;
}

:deep(.v-btn-group .v-btn) {
	font-weight: 400;
	text-transform: none;
	letter-spacing: normal;
}
</style>
